import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useAddParticipant, useMyParticipants } from "../lib/queries";
import { ErrorBox, Loading } from "../components/Loading";
import { CoachPickerSheet } from "../components/CoachPickerSheet";
import { closeApp } from "../lib/tg";
import { useToast } from "../components/Toast";

export default function Kids() {
  const { user } = useAuth();
  const toast = useToast();
  const q = useMyParticipants(user?.parent_id ?? undefined);
  const add = useAddParticipant();
  const [name, setName] = useState("");
  const [year, setYear] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [pickingCoachFor, setPickingCoachFor] = useState<{ id: string; name: string; coach_ids: string[] } | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox message={(q.error as Error).message} />;

  const kids = q.data ?? [];
  const hasKids = kids.length > 0;
  const showForm = !hasKids || formOpen;

  const submit = async () => {
    try {
      const created = await add.mutateAsync({
        parent_id: user!.parent_id!,
        name: name.trim(),
        birth_year: year ? Number(year) : null,
      });
      setName(""); setYear("");
      setFormOpen(false);
      toast.success(`✅ ${created.name} додано`);
      // Prompt to pick coach right away.
      setPickingCoachFor({ id: created.id, name: created.name, coach_ids: [] });
    } catch (e) {
      toast.error(`Не вдалося додати: ${(e as Error).message}`);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">Діти</h1>

      {hasKids && (
        <ul className="space-y-2">
          {kids.map((k) => {
            const coaches = k.coaches ?? [];
            return (
              <li key={k.id} className="card">
                <div className="flex justify-between items-baseline">
                  <Link to={`/me/kid/${k.id}`} className="font-medium text-tg-link">
                    {k.name} →
                  </Link>
                  {k.birth_year && <span className="text-tg-hint text-sm">{k.birth_year}</span>}
                </div>
                <div className="text-sm mt-1">
                  {coaches.length === 0 ? (
                    <button
                      className="text-tg-link"
                      onClick={() => setPickingCoachFor({ id: k.id, name: k.name, coach_ids: [] })}
                    >+ Обрати тренера</button>
                  ) : (
                    <>
                      <span className="text-tg-hint">Тренер{coaches.length > 1 ? "и" : ""}: </span>
                      {coaches.map((c) => c.name).join(", ")}
                      <button
                        className="text-tg-link ml-2"
                        onClick={() => setPickingCoachFor({
                          id: k.id,
                          name: k.name,
                          coach_ids: coaches.map((c) => c.id),
                        })}
                      >змінити</button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showForm ? (
        <section className="card">
          <h2 className="font-semibold mb-2">{hasKids ? "Додати ще одну дитину" : "Додати дитину"}</h2>
          <div className="space-y-2">
            <input
              className="w-full bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
              placeholder="Ім'я та прізвище"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={hasKids}
            />
            <input
              className="w-full bg-tg-bg border border-tg-secondary rounded-xl px-3 py-2"
              placeholder="Рік народження (необов'язково)"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value.replace(/\D/g, ""))}
            />
            <div className="flex gap-2">
              {hasKids && (
                <button
                  className="btn-secondary flex-1"
                  onClick={() => { setFormOpen(false); setName(""); setYear(""); }}
                >Скасувати</button>
              )}
              <button
                className="btn-primary flex-1"
                disabled={!name.trim() || add.isPending || !user?.parent_id}
                onClick={submit}
              >Додати</button>
            </div>
          </div>
        </section>
      ) : (
        <div className="flex gap-2">
          <button className="btn-secondary flex-1" onClick={() => setFormOpen(true)}>
            + Додати ще одну дитину
          </button>
          <button className="btn-primary flex-1" onClick={() => closeApp()}>Готово</button>
        </div>
      )}

      {pickingCoachFor && (
        <CoachPickerSheet
          participant={pickingCoachFor}
          onClose={() => setPickingCoachFor(null)}
        />
      )}
    </div>
  );
}
